import api from "@/api";
import { defineStore } from "pinia";
import { useFilterStore } from "./filterStore";
import {
  Subject,
  AssessmentType,
  Thread,
  Concept,
} from "@/interfaces/subject.interface";

import matric from "@/utils/learner-report/thread-layouts/matric-foundation.json";
import juniorHigh from "@/utils/learner-report/thread-layouts/junior-high.json";
import seniorPrimary from "@/utils/learner-report/thread-layouts/senior-primary.json";
import seniorPrimaryV2 from "@/utils/learner-report/thread-layouts/senior-primary-v2.json";
import juniorPrimary from "@/utils/learner-report/thread-layouts/junior-primary.json";
import adultNumeracy from "@/utils/learner-report/thread-layouts/adult-numeracy.json";
import englishJuniorHigh from "@/utils/learner-report/thread-layouts/english-junior-high.json";
import englishSeniorPrimary from "@/utils/learner-report/thread-layouts/english-senior-primary.json";

const concepts = (assessmentType: string) => {
  if (assessmentType === "MATHS_SENIOR_PRIMARY") {
    return seniorPrimary;
  } else if (assessmentType === "MATHS_SENIOR_PRIMARY_V2") {
    return seniorPrimaryV2;
  } else if (assessmentType === "MATHS_JUNIOR_PRIMARY") {
    return juniorPrimary;
  } else if (assessmentType === "MATHS_MATRIC_FOUNDATIONS") {
    return matric;
  } else if (assessmentType === "MATHS_ADULT_NUMERACY") {
    return adultNumeracy;
  } else if (assessmentType === "ENGLISH_JUNIOR_HIGH") {
    return englishJuniorHigh;
  } else if (assessmentType === "ENGLISH_SENIOR_PRIMARY") {
    return englishSeniorPrimary;
  } else {
    return juniorHigh;
  }
};

export const useSubjectStore = defineStore({
  id: "subjectStore",
  state: (): { loading: boolean; subjects: Subject[] } => ({
    loading: false,
    subjects: [] as Subject[],
  }),
  getters: {
    getSubjects(state): Subject[] {
      return state.subjects;
    },
    isLoading(state): boolean {
      return state.loading;
    },
    getThreads(state): Thread[] {
      const filterStore = useFilterStore();

      const subject = state.subjects.find(
        (s: Subject) =>
          s.name.toUpperCase() ===
          filterStore?.selectedSubject.name.toUpperCase()
      ) as Subject;

      const assessmentType = subject.assessmentTypes.find(
        (at: AssessmentType) => at.grades.includes(filterStore?.selectedGrade)
      );

      if (!assessmentType) return [];

      return assessmentType.threads;
    },
    getConcepts(state): any {
      const filterStore = useFilterStore();

      const subject = state.subjects.find(
        (s: Subject) =>
          s.name.toUpperCase() ===
          filterStore?.selectedSubject.name.toUpperCase()
      ) as Subject;

      const assessmentType = subject.assessmentTypes.find(
        (at: AssessmentType) => at.grades.includes(filterStore?.selectedGrade)
      );

      if (!assessmentType) return [];

      return concepts(assessmentType.type);
    },
    getAssessmentTypes(state): AssessmentType[] {
      const filterStore = useFilterStore();

      const subject = state.subjects.find(
        (s: Subject) =>
          s.name.toUpperCase() ===
          filterStore?.selectedSubject.name.toUpperCase()
      ) as Subject;

      return subject.assessmentTypes;
    },
    getLevelMultiplier(state): number {
      const filterStore = useFilterStore();

      const subject = state.subjects.find(
        (s: Subject) =>
          s.name.toUpperCase() ===
          filterStore?.selectedSubject.name.toUpperCase()
      ) as Subject;

      const assessmentType = subject.assessmentTypes.find(
        (at: AssessmentType) => at.grades.includes(filterStore?.selectedGrade)
      );

      if (!assessmentType) return 3;

      return assessmentType.levelMultiplier;
    },
    getLevelMultiplierByAssessmentType(state): (assessmentType: string) => number {
      return (type: string) => {
        const subject = state.subjects.find((s: Subject) => s.assessmentTypes.find((at: AssessmentType) => at.type === type));

        const assessmentType = subject?.assessmentTypes.find((at: AssessmentType) => at.type === type);

        if (!assessmentType) return 3;

        return assessmentType.levelMultiplier;
      };
    },
    getSubjectNameById(state): (id: string) => string {
      return (id: string) => {
        const subject = state.subjects.find((s: Subject) => s.id === id);

        return subject?.name || "";
      };
    },
    // Get the subject ID by its name from the subject store
    getSubjectIdByName(state): (name: string) => string {
      return (name: string) => {
        const subject = state.subjects.find((s: Subject) => s.name.toLowerCase() === name.toLowerCase());

        return subject?.id || "";
      };
    },
    //get concepts by assessment type
    getConceptsByAssessmentType(state): (type: string) => any {
      return (type: string) => concepts(type);
    }
  },
  actions: {
    async fetchSubjects() {
      this.loading = true;
      try {
        // if (this.subjects.length > 0) {
        const subjectsResponse = await api.get("/v2/organisation/subject/all");

        if (subjectsResponse.status !== 200) {
          throw new Error("Failed to fetch subjects from API.");
        }

        const subjectsData = subjectsResponse.data.data;
        this.subjects = subjectsData;
        // }
      } catch (error: any) {
        console.log("Error: ", error);

        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loading = false;
      }
    },
  },
  persist: true,
});
